import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { Link } from 'react-router-dom';

import { useGetBeneficiaryVirtualAccountsQuery } from '@/api/v1';
import { BalanceWidget } from '@/components/molecules/BalanceWidget';
import { usePermissionsManager } from '@/hooks/usePermissionsManager';
import { useTranslator } from '@/hooks/useTranslator';
import { DetailedBalance } from '@/types/bankInfo';
import { CurrencyCodeType } from '@/types/currency';
import { Permission } from '@/types/permissions';

interface Props {
  readonly balances?: readonly DetailedBalance[];
  readonly beneficiaryId?: string;
}

export const BalanceLayout: React.FC<Props> = (props: Props): React.ReactElement => {
  const { balances = [DetailedBalance.zero(CurrencyCodeType.USDC)], beneficiaryId } = props;
  const { userIsAuthorizedTo } = usePermissionsManager();
  const tr = useTranslator();

  const { data: virtualAccounts = [] } = useGetBeneficiaryVirtualAccountsQuery(
    beneficiaryId ?? '',
    { skip: !beneficiaryId },
  );
  const hasUsdVirtualAccount = virtualAccounts.length > 0;

  const labelForCurrency = React.useCallback(
    (code: CurrencyCodeType | string): string => {
      switch (code) {
        case CurrencyCodeType.USD:
          return tr('USD_BALANCE');
        case CurrencyCodeType.USDC:
          return tr('USDC_WALLET');
        case CurrencyCodeType.USDT:
          return tr('USDT_WALLET');
        case CurrencyCodeType.MXNB:
          return tr('MXNB_WALLET');
        case CurrencyCodeType.BRL1:
          return tr('BRL1_WALLET');
        default:
          return tr('BALANCE');
      }
    },
    [tr],
  );

  const canViewUsdAccount = userIsAuthorizedTo(Permission.VIEW_USD_ACCOUNT);
  const balanceList = balances.filter(
    (balance: DetailedBalance): boolean =>
      balance.currencyCode !== CurrencyCodeType.USD || canViewUsdAccount,
  );

  return (
    <Box display="flex" flexDirection="column" px={4} py={5} gap={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">{tr('YOUR_BALANCES')}</Typography>
        {userIsAuthorizedTo(Permission.CREATE_TRANSACTIONS) && (
          <ButtonBase component={Link} to="convert" sx={styles.button}>
            <Typography fontSize={16} fontWeight={400} lineHeight="24px">
              {tr('CONVERT')}
            </Typography>
          </ButtonBase>
        )}
      </Box>
      {balanceList.map((balance: DetailedBalance): React.ReactElement => {
        const isUsd = balance.currencyCode === CurrencyCodeType.USD;

        const actionHref = isUsd
          ? hasUsdVirtualAccount
            ? '/accounts/org?walletCurrency=USD'
            : '/accounts/org'
          : `/accounts/org?walletCurrency=${balance.currencyCode}`;

        return (
          <Box key={balance.currencyCode} sx={styles.balanceRow}>
            <Box sx={styles.balanceWidgetWrapper}>
              <BalanceWidget
                balance={balance}
                showDetailsButton={false}
                label={labelForCurrency(balance.currencyCode)}
                actionLabel={tr('MANAGE')}
                actionHref={actionHref}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  button: {
    height: 40,
    width: 110,
    lineHeight: '40px',
    backgroundColor: 'custom.InputsBackground',
    borderRadius: 5,
  },
  balanceRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  balanceWidgetWrapper: {
    flex: 1,
  },
};
